// Library Methods
import { useEffect, useState } from "react";

// Styles
import { PageContainerWrapper } from "src/styles/mui/muiStylesHelper";

// Components
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import ClinicStats from "src/components/containers/Dashboard/ClinicStats/ClinicStats";
import DashboardDevices from "src/components/containers/Dashboard/DashboardDevices/DashboardDevices";
import RecentlyCompletedAccordion from "src/components/containers/Dashboard/RecentlyCompletedAccordion/RecentlyCompletedAccordion";
import TodayAccordion from "src/components/containers/Dashboard/TodayAccordion/TodayAccordion";
import SnackbarMessage from "src/components/shared/alerts/SnackbarMessage/SnackbarMessage";
import WelcomeModal from "src/components/containers/Dashboard/WelcomeModal/WelcomeModal";

// Contexts
import { ExamSliderProvider } from "src/contexts/exams/ExamSliderContext";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

const Dashboard = () => {
  // Controls value for the "last X number of days"
  // 0 = Today, 7 = Last 7 Days, etc.
  const [timeRange, setTimeRange] = useState(0);

  const theme = useTheme();
  const { snackbarInfo, setSnackbarInfo } = useSnackbarContext();

  const [isLoadingRecentlyCompleted, setIsLoadingRecentlyCompleted] =
    useState(true);
  const [isLoadingToday, setIsLoadingToday] = useState(true);
  const isExamsColumnReady = !isLoadingRecentlyCompleted && !isLoadingToday;

  // Remount DashboardDevices from changing the browser size
  const [devicesRemountKey, setDevicesRemountKey] = useState(0);

  useEffect(() => {
    const mdQuery = window.matchMedia(
      theme.breakpoints.up("md").replace("@media ", "")
    );
    const handleBreakpointChange = () =>
      setDevicesRemountKey((prev) => prev + 1);

    mdQuery.addEventListener("change", handleBreakpointChange);
    return () => mdQuery.removeEventListener("change", handleBreakpointChange);
  }, [theme]);

  // Remount DashboardDevices from changing the browser zoom level
  useEffect(() => {
    let zoomQuery: MediaQueryList;

    const subscribeToZoomChange = () => {
      zoomQuery = window.matchMedia(
        `(resolution: ${window.devicePixelRatio}dppx)`
      );
      zoomQuery.addEventListener("change", handleZoomChange, { once: true });
    };

    const handleZoomChange = () => {
      setDevicesRemountKey((prev) => prev + 1);
      subscribeToZoomChange();
    };

    subscribeToZoomChange();
    return () => zoomQuery.removeEventListener("change", handleZoomChange);
  }, []);

  return (
    <Box
      id="dashboard-page-content"
      sx={{
        ...PageContainerWrapper(theme),
        padding: "0px",
        flexDirection: {
          xs: "column",
          md: "row",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          backgroundColor:
            theme.palette.mode === "dark" ? "#4D5063" : "#5B6D7A40",
          padding: { xs: "16px", md: "16px 16px 0px 16px" },
          maxWidth: { xs: "100%", md: "350px" },
          minHeight: "100%",
          gap: "16px",
        }}
      >
        <ClinicStats timeRange={timeRange} />
        <DashboardDevices
          key={devicesRemountKey}
          isExamsColumnReady={isExamsColumnReady}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column" },
          flex: 1,
          maxHeight: "fit-content",
          gap: "16px",
          padding: "16px",
        }}
      >
        <Box width={{ xs: "100%" }} maxHeight="fit-content">
          <RecentlyCompletedAccordion
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            onLoadingChange={setIsLoadingRecentlyCompleted}
          />
        </Box>

        <Box width={{ xs: "100%" }} maxHeight="fit-content">
          <ExamSliderProvider>
            <TodayAccordion
              timeRange={timeRange}
              onLoadingChange={setIsLoadingToday}
            />
          </ExamSliderProvider>
        </Box>
      </Box>

      {/* Snackbar success and error messages */}
      <SnackbarMessage
        snackbarInfo={snackbarInfo}
        setSnackbarInfo={setSnackbarInfo}
      />

      <WelcomeModal />
    </Box>
  );
};

export default Dashboard;
